<script setup>
import "daterangepicker/daterangepicker.css";
import "daterangepicker/daterangepicker.js";
import moment from "moment";
import DateRangePicker from "daterangepicker";

import { ref, onMounted, computed } from "vue";
import { usePaymentStore } from "@/stores/paymentStore";

const title = ref("Payments");
const text = ref("Finance & Accounts");
const text1 = ref("Payments");

const paymentStore = usePaymentStore();
const loading = computed(() => paymentStore.loading);
const meta = computed(() => paymentStore.meta);
const items = computed(() => paymentStore.items);
const summary = computed(() => paymentStore.summary);

const sortLabel = ref("Last 7 Days");
const dateRangeInput = ref(null);

function toggleHeader() {
  document.getElementById("collapse-header")?.classList.toggle("active");
  document.body.classList.toggle("header-collapse");
}

function applyFilters(page = 1) {
  paymentStore.fetchAll({ page });
}

function handlePageChange(page) {
  applyFilters(page);
}

function setSort(sort, label) {
  paymentStore.filters.sort = sort;
  sortLabel.value = label;
  applyFilters(1);
}

function downloadBlob(blob, filename) {
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.URL.revokeObjectURL(url);
}

async function handleExportPdf() {
  try {
    const blob = await paymentStore.exportPdf();
    downloadBlob(blob, `invoice-payments-${Date.now()}.pdf`);
  } catch {
    // toast already handled
  }
}

async function handleExportExcel() {
  try {
    const blob = await paymentStore.exportExcel();
    downloadBlob(blob, `invoice-payments-${Date.now()}.xlsx`);
  } catch {
    // toast already handled
  }
}

// money formatter for cards
function formatMoney(value) {
  const num = Number(value || 0);
  return num.toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

const avgPayment = computed(() => {
  if (!summary.value.count) return 0;
  return summary.value.total_amount / summary.value.count;
});

onMounted(async () => {
  const start = moment().subtract(6, "days");
  const end = moment();

  paymentStore.filters.from = start.format("YYYY-MM-DD");
  paymentStore.filters.to = end.format("YYYY-MM-DD");

  if (dateRangeInput.value) {
    new DateRangePicker(
      dateRangeInput.value,
      {
        startDate: start,
        endDate: end,
        ranges: {
          Today: [moment(), moment()],
          Yesterday: [
            moment().subtract(1, "days"),
            moment().subtract(1, "days"),
          ],
          "Last 7 Days": [moment().subtract(6, "days"), moment()],
          "Last 30 Days": [moment().subtract(29, "days"), moment()],
          "This Month": [moment().startOf("month"), moment().endOf("month")],
          "Last Month": [
            moment().subtract(1, "month").startOf("month"),
            moment().subtract(1, "month").endOf("month"),
          ],
        },
      },
      function booking_range(start, end) {
        if (dateRangeInput.value) {
          dateRangeInput.value.value =
            start.format("DD/MM/YYYY") + " - " + end.format("DD/MM/YYYY");
        }
        paymentStore.filters.from = start.format("YYYY-MM-DD");
        paymentStore.filters.to = end.format("YYYY-MM-DD");
        applyFilters(1);
      }
    );

    dateRangeInput.value.value =
      start.format("DD/MM/YYYY") + " - " + end.format("DD/MM/YYYY");
  }

  await applyFilters(1);
});
</script>

<template>
  <layout-header />
  <layout-sidebar />

  <!-- Page Wrapper -->
  <div class="page-wrapper">
    <div class="content">
      <!-- Breadcrumb -->
      <div
        class="d-md-flex d-block align-items-center justify-content-between page-breadcrumb mb-3"
      >
        <breadcrumb :title="title" :text="text" :text1="text1" />
        <div
          class="d-flex my-xl-auto right-content align-items-center flex-wrap"
        >
          <!-- Export -->
          <div class="mb-2">
            <div class="dropdown">
              <a
                href="javascript:void(0);"
                class="dropdown-toggle btn btn-white d-inline-flex align-items-center"
                data-bs-toggle="dropdown"
              >
                <i class="ti ti-file-export me-1"></i>Export
              </a>
              <ul class="dropdown-menu dropdown-menu-end p-3">
                <li>
                  <a
                    href="javascript:void(0);"
                    class="dropdown-item rounded-1"
                    @click.prevent="handleExportPdf"
                    v-can="'Payment.Export'"
                  >
                    <i class="ti ti-file-type-pdf me-1"></i>Export as PDF
                  </a>
                </li>
                <li>
                  <a
                    href="javascript:void(0);"
                    class="dropdown-item rounded-1"
                    @click.prevent="handleExportExcel"
                    v-can="'Payment.Export'"
                  >
                    <i class="ti ti-file-type-xls me-1"></i>Export as Excel
                  </a>
                </li>
              </ul>
            </div>
          </div>

          <div class="head-icons ms-2">
            <a
              href="javascript:void(0);"
              data-bs-toggle="tooltip"
              data-bs-placement="top"
              data-bs-original-title="Collapse"
              id="collapse-header"
              @click="toggleHeader"
            >
              <i class="ti ti-chevrons-up"></i>
            </a>
          </div>
        </div>
      </div>
      <!-- /Breadcrumb -->


            <!-- Summary cards -->
      <div class="row mb-3">
        <div class="col-md-3 col-sm-6 mb-3">
          <div class="card h-100">
            <div class="card-body">
              <p class="mb-1 text-muted">Total Payments</p>
              <h4 class="mb-0">{{ summary.count }}</h4>
            </div>
          </div>
        </div>
        <div class="col-md-3 col-sm-6 mb-3">
          <div class="card h-100">
            <div class="card-body">
              <p class="mb-1 text-muted">Amount Received</p>
              <h4 class="mb-0">
                {{ formatMoney(summary.total_amount) }}
              </h4>
              <p class="text-muted mb-0 fs-12">
                Within selected date range
              </p>
            </div>
          </div>
        </div>
        <div class="col-md-3 col-sm-6 mb-3">
          <div class="card h-100">
            <div class="card-body">
              <p class="mb-1 text-muted">Average Payment</p>
              <h4 class="mb-0">
                {{ formatMoney(avgPayment) }}
              </h4>
              <p class="text-muted mb-0 fs-12">
                Total / Payments
              </p>
            </div>
          </div>
        </div>
        <div class="col-md-3 col-sm-6 mb-3">
          <div class="card h-100">
            <div class="card-body">
              <p class="mb-1 text-muted">Current Filter</p>
              <p class="mb-0">
                <span class="badge bg-light text-dark">
                  {{ sortLabel }}
                </span>
              </p>
            </div>
          </div>
        </div>
      </div>
      <!-- /Summary cards -->


      <div class="card">
        <div
          class="card-header d-flex align-items-center justify-content-between flex-wrap row-gap-3"
        >
          <h5>Payment List</h5>
          <div
            class="d-flex my-xl-auto right-content align-items-center flex-wrap row-gap-3"
          >
            <!-- Date range -->
            <div class="me-3">
              <div class="input-icon-end position-relative">
                <input
                  type="text"
                  class="form-control date-range bookingrange"
                  ref="dateRangeInput"
                  placeholder="dd/mm/yyyy - dd/mm/yyyy"
                  readonly
                />
                <span class="input-icon-addon">
                  <i class="ti ti-chevron-down"></i>
                </span>
              </div>
            </div>

            <!-- Sort dropdown -->
            <div class="dropdown">
              <a
                href="javascript:void(0);"
                class="dropdown-toggle btn btn-white d-inline-flex align-items-center"
                data-bs-toggle="dropdown"
              >
                Sort By : {{ sortLabel }}
              </a>
              <ul class="dropdown-menu dropdown-menu-end p-3">
                <li>
                  <a
                    href="javascript:void(0);"
                    class="dropdown-item rounded-1"
                    @click.prevent="setSort('recent', 'Recently Added')"
                    >Recently Added</a
                  >
                </li>
                <li>
                  <a
                    href="javascript:void(0);"
                    class="dropdown-item rounded-1"
                    @click.prevent="
                      setSort('amount_asc', 'Amount (Low to High)')
                    "
                    >Amount (Low to High)</a
                  >
                </li>
                <li>
                  <a
                    href="javascript:void(0);"
                    class="dropdown-item rounded-1"
                    @click.prevent="
                      setSort('amount_desc', 'Amount (High to Low)')
                    "
                    >Amount (High to Low)</a
                  >
                </li>
                <li>
                  <a
                    href="javascript:void(0);"
                    class="dropdown-item rounded-1"
                    @click.prevent="setSort('oldest', 'Oldest First')"
                    >Oldest</a
                  >
                </li>
              </ul>
            </div>
          </div>
        </div>

        <div class="card-body p-0">
          <div class="custom-datatable-filter table-responsive">
            <payments-table
              :items="items"
              :loading="loading"
              :meta="meta"
              @page-change="handlePageChange"
            />
          </div>
        </div>
      </div>
    </div>

    <div
      class="footer d-sm-flex align-items-center justify-content-between border-top bg-white p-3"
    >
      <p class="mb-0">2014 - {{ new Date().getFullYear() }} &copy; DoneQuick HR & Payroll</p>
      <p>
        Powered by
        <a href="javascript:void(0);" class="text-primary"
          >DoneQuick Technological World</a
        >
      </p>
    </div>
  </div>
</template>


